import React from 'react'
import { Alert, Card } from 'react-bootstrap'
import { DateTime } from 'luxon'
import { Listing } from '../ui/Listing/Listing'

import { AppContext } from '../../App'
import * as Request from '../../utilities/request'
import { XpmSyncFailure } from '../../../../back-end/common/xpmSyncFailure'
import { XpmClient } from '../../../../back-end/common/xpm/client'
import { XpmJob } from '../../../../back-end/common/xpm/job'

import './failedSyncCard.css'
import { InvoiceFilterBadge } from '../invoice/invoiceFilterBadge'
import { XpmTime } from '../../../../back-end/common/xpm/time'
import { Time } from '../../../../back-end/common/time'
import * as Helpers from '../../screens/xero/helpers'

const FailedSyncCard = () => {
	const { appState } = React.useContext(AppContext)
	const [syncFailures, setSyncFailures] = React.useState<XpmSyncFailure[] | null>(null)
	const [messageTime, setMessageTime] = React.useState<DateTime | null>(null)
	const [statusFilter, setStatusFilter] = React.useState<string>('all')

	const refreshData = React.useCallback(() => {
		const getData = async () => {
			const xpmSyncFailureRes = await Request.get('failures')
			setSyncFailures(xpmSyncFailureRes.data.xpmSyncFailures)
		}

		if (appState.authState.isLoggedIn) {
			getData()
		}
	}, [appState.authState, setSyncFailures])

	React.useEffect(() => {
		refreshData()

		const interval = setInterval(() => {
			refreshData()
		}, 60000)

		return () => clearInterval(interval) // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
	}, [appState.authState, refreshData])

	const handleRetryClients = React.useCallback(
		async (selected: string[]) => {
			const clients = syncFailures?.filter((sf) => sf.object === 'client' && selected.includes(sf.xpmSyncFailureID)) || []
			if (clients.length > 0) {
				const chosenOrg = clients[0].xeroOrgID
				Request.post('failures/retry', { clients: clients.filter((c) => c.xeroOrgID === chosenOrg).map((c) => c.xpmID), xeroOrgID: chosenOrg })
				setMessageTime(DateTime.now())
			}
		},
		[appState.authState, syncFailures]
	)

	const handleRetryJobs = React.useCallback(
		async (selected: string[]) => {
			const jobs = syncFailures?.filter((sf) => sf.object === 'job' && selected.includes(sf.xpmSyncFailureID)) || []
			if (jobs.length > 0) {
				const chosenOrg = jobs[0].xeroOrgID
				Request.post('failures/retry', {
					jobs: jobs.filter((j) => j.xeroOrgID === chosenOrg).map((j) => (j.item as { ID: string }).ID),
					xeroOrgID: chosenOrg,
				})
				setMessageTime(DateTime.now())
			}
		},
		[appState.authState, syncFailures]
	)

	const handleRetryTimes = React.useCallback(
		async (selected: string[]) => {
			const times = syncFailures?.filter((sf) => sf.object === 'time' && selected.includes(sf.xpmSyncFailureID)) || []
			if (times.length > 0) {
				const chosenOrg = times[0].xeroOrgID
				Request.post('failures/retry', { times: times.filter((t) => t.xeroOrgID === chosenOrg).map((t) => t.xpmID), xeroOrgID: chosenOrg })
				setMessageTime(DateTime.now())
			}
		},
		[appState.authState, syncFailures]
	)

	const handleFullSync = React.useCallback(async () => {
		const req = await Request.get(`organisation?where=AccountXeroOrgIsBilly==1`)
		const cons = Helpers.groupConnectionsByUser(req.data.accountXeroOrgs).flatMap((c) => c.orgs.filter((org) => org.apiProvider === 'xpm'))
		Request.get(`syncxpm?where=UserAccountXeroOrgAuthID=in(${cons.map((c) => c.userAccountXeroOrgAuthID).join(',')})`)
		setMessageTime(DateTime.now())
	}, [appState.authState])

	const handleFilterClick = (filter: string) => setStatusFilter(filter)

	const filteredSyncFailures = syncFailures?.filter((sf) => statusFilter === 'all' || sf.object + 's' === statusFilter) || []

	return (
		<Card className="p-3">
			{messageTime !== null && (
				<Alert>
					This can take up to 15 minutes, please do not retry <strong>anything</strong> until at least{' '}
					{messageTime.plus({ minutes: 15 }).toFormat('hh:mm')} or you may cause the process to fail.
				</Alert>
			)}
			<Listing<XpmSyncFailure>
				name="Sync Failure"
				namePlural="Sync Failures"
				columns={[
					{
						value: (item) => item.createTs,
						render: (item) => <div>{DateTime.fromISO(item.createTs).toFormat('dd MMM yyyy')}</div>,
						showHeader: true,
						headerText: 'Date of Issue',
						sortColumnName: 'date',
						width: 'auto',
					},
					{
						value: (item) => item.object,
						render: (item) => <div>{item.object}</div>,
						showHeader: true,
						headerText: 'Object',
						sortColumnName: 'object',
						width: 'auto',
					},
					{
						value: (item) => getItemDetails(item),
						render: (item) => <div>{getItemDetails(item)}</div>,
						showHeader: true,
						headerText: 'Details',
						sortColumnName: 'details',
					},
					{
						value: (item) => item.message,
						render: (item) => <div>{item.message}</div>,
						showHeader: true,
						headerText: 'Error',
						sortColumnName: 'error',
					},
				]}
				selectedActions={[
					{ name: 'Rerun Sync', method: handleFullSync },
					{ name: 'Retry Clients', method: handleRetryClients },
					{ name: 'Retry Jobs', method: handleRetryJobs },
					{ name: 'Retry Times', method: handleRetryTimes },
				]}
				customFilters={[
					<InvoiceFilterBadge
						key="all"
						text="All"
						amount={syncFailures?.length || 0}
						handleClick={handleFilterClick}
						selected={statusFilter === 'all'}
					/>,
					<InvoiceFilterBadge
						key="clients"
						text="Clients"
						amount={syncFailures?.filter((sf) => sf.object === 'client')?.length || 0}
						handleClick={handleFilterClick}
						selected={statusFilter === 'clients'}
					/>,
					<InvoiceFilterBadge
						key="jobs"
						text="Jobs"
						amount={syncFailures?.filter((sf) => sf.object === 'job')?.length || 0}
						handleClick={handleFilterClick}
						selected={statusFilter === 'jobs'}
					/>,
					<InvoiceFilterBadge
						key="times"
						text="Times"
						amount={syncFailures?.filter((sf) => sf.object === 'time')?.length || 0}
						handleClick={handleFilterClick}
						selected={statusFilter === 'times'}
					/>,
				]}
				paginationControls="bottom"
				getIDFunc={(item) => item.xpmSyncFailureID}
				list={filteredSyncFailures}
				defaultSort={{ column: 'date', order: 'DSC' }}
				isLoading={syncFailures === null}
			/>
		</Card>
	)
}

const getItemDetails = (item: XpmSyncFailure) => {
	if (item.object === 'client') {
		const data = item.item as XpmClient
		return data.Name
	} else if (item.object === 'job') {
		const data = item.item as XpmJob
		return `[${data.ID}] ${data.Name}`
	} else if (item.object === 'time') {
		const dataItem = item.item as any
		if (dataItem.Staff) {
			const data = item.item as XpmTime
			return `${data.Staff.Name} - ${DateTime.fromISO(data.Date).toFormat('dd MMM yyyy')}`
		} else if (dataItem.Name) {
			const data = item.item as Time & { Name: string }
			return `${data.Name} - ${DateTime.fromISO(data.date).toFormat('dd MMM yyyy')}`
		}
	}
	return ''
}

export default FailedSyncCard
